// URLSearchParams を使うモジュールと同居させて、利用側での入れ忘れを防ぐ(ネイティブ実装があれば何もしない)
import 'url-search-params-polyfill'

import { conditionQueryKeys, ItemListCondition, serializeForUrl } from './condition'

/**
 * /itemlist のページURL・canonical URLのクエリ合成。
 *
 * URLクエリは3種類あり、所有者が異なる:
 * - 絞り込み条件のキー: conditionモデルが所有(語彙・正規形・並び順はmodel/condition.tsの責務)
 * - parts(ページパーツ指定): 本モジュールが解釈する(parsePageParts参照)
 * - 未知のキー(計測パラメータ等): hl-itemlistは関知しない
 *
 * 本モジュールが担うのはpartsの解釈と、これらの合成の規則:
 * - ページURL(applyConditionToSearch): 現在のクエリへ条件を適用する。上書きするのは条件のキーだけで、
 *   parts・未知のキーは値も並びも保持する(条件を変更しても計測クエリ等を削らない)
 * - canonical(canonicalSearch): 「条件 + parts」だけから組み立て、未知のキーは含めない
 */

/**
 * ページパーツ(`parts`)クエリの解釈。
 *
 * partsはページ上部に表示するフリーコンテンツ(ページパーツ)の指定で、BFFでなくfrontの語彙
 * (値はパーツ名=パス名の末尾)。**絞り込み条件ではない**ためItemListConditionには含めない。
 * 取得と描画はserver(Hl/ItemlistController)がSSRで行い、client側の役割は
 * 「表示内容が変わる条件として正規URL・canonicalにこの値を維持する」ことだけ(canonicalSearch参照)。
 * BFFへは送らない。
 */
export function parsePageParts(search: string): string {
  return (new URLSearchParams(search).get('parts') || '').trim()
}

/**
 * 条件の変更を現在のURLクエリ(location.search)へ適用した、遷移先URL用のクエリ文字列を返す。
 * 条件のキーを取り除いて残りをそのまま保持し、後ろに条件の正規形クエリ(serializeForUrl)をつなげる。
 */
export function applyConditionToSearch(condition: ItemListCondition, search: string): string {
  const preserved = new URLSearchParams(search)
  conditionQueryKeys.forEach(key => preserved.delete(key))

  return [preserved.toString(), serializeForUrl(condition)]
    .filter(part => part !== '')
    .join('&')
}

/**
 * canonical URL用のクエリ文字列を返す。
 * 同一の「条件 + parts」が(元のURLの記載順によらず)常に同一のバイト列になるよう、
 * partsも条件のキーと合わせて全体をキーの辞書順に並べる。
 */
export function canonicalSearch(condition: ItemListCondition, parts: string): string {
  const pairs = serializeForUrl(condition).split('&').filter(pair => pair !== '')

  if (parts !== '') {
    // 条件のクエリとエンコード方式を揃えるため、partsもURLSearchParams経由で文字列化する
    const partsParam = new URLSearchParams()
    partsParam.append('parts', parts)
    pairs.push(partsParam.toString())
  }

  // キーは英字のみ・重複なしのため、`key=value` の文字列比較がキーの辞書順に一致する
  // (キーの終端 `=` は英字より小さく、前方一致するキーは短いほうが先になる)
  return pairs.sort().join('&')
}
